import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getAuthToken } from '../../services/api';
import './ParentSections.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const ParentSectionPayments = () => {
  const { t } = useTranslation('parent');
  const [activeTab, setActiveTab] = useState('subscriptions');
  const [subscriptions, setSubscriptions] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Get user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const parentId = user.id;

  // Fetch subscriptions and payments
  useEffect(() => {
    const fetchData = async () => {
      if (!parentId) {
        setLoading(false);
        return;
      }

      try {
        const token = getAuthToken();

        // Single source of truth: GET /api/me/subscription (same as UserSectionSubscription)
        const subscriptionsResponse = await fetch(`${API_BASE_URL}/api/me/subscription`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (subscriptionsResponse.ok) {
          const subscriptionsData = await subscriptionsResponse.json();
          const data = subscriptionsData.data;
          if (subscriptionsData.success && data) {
            const durationDays = data.duration_days != null ? Number(data.duration_days) : 30;
            const endsAt = data.ends_at ? new Date(data.ends_at) : null;
            const isActive = data.status === 'active' && endsAt && endsAt > new Date();
            setSubscriptions([{
              id: data.subscription_id,
              planId: data.plan_id,
              planName: data.plan_name || '',
              children: [],
              status: isActive ? 'active' : (data.status || 'expired'),
              price: data.price_at_purchase != null ? Number(data.price_at_purchase) : null,
              billingCycle: durationDays >= 365 ? 'yearly' : 'monthly',
              startDate: data.starts_at,
              expiryDate: data.ends_at,
              autoRenew: false
            }]);
          }
        }

        // Fetch payment history using new API
        const paymentsResponse = await fetch(`${API_BASE_URL}/api/subscriptions/billing-history`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (paymentsResponse.ok) {
          const paymentsData = await paymentsResponse.json();
          if (paymentsData.success) {
            setPaymentHistory(paymentsData.billingHistory || []);
          }
        }
      } catch (error) {
        console.error('Error fetching payments data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [parentId]);

  return (
    <div className="parent-main-content">
      <div className="parent-content-header">
        <h1 className="parent-content-title">{t('payments.title')}</h1>
        <p className="parent-content-subtitle">{t('payments.subtitle')}</p>
      </div>

      {/* Tabs */}
      <div className="parent-tabs">
        <button
          className={`parent-tab ${activeTab === 'subscriptions' ? 'active' : ''}`}
          onClick={() => setActiveTab('subscriptions')}
        >
          <i className="material-icons">subscriptions</i>
          {t('payments.subscriptions')}
        </button>
        <button
          className={`parent-tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          <i className="material-icons">history</i>
          {t('payments.paymentHistory')}
        </button>
      </div>

      {/* Subscriptions Tab */}
      {activeTab === 'subscriptions' && (
        <div className="parent-section">
          <div className="parent-section-header">
            <h2 className="parent-section-title">{t('payments.activeSubscriptions')}</h2>
          </div>

          {subscriptions.length === 0 ? (
            <div className="parent-empty-state">
              <i className="material-icons">subscriptions</i>
              <p>{t('payments.noSubscriptions')}</p>
              <button className="parent-btn primary">
                <i className="material-icons">add</i>
                {t('payments.subscribe')}
              </button>
            </div>
          ) : (
            <div className="parent-subscriptions-list">
              {subscriptions.map((subscription) => (
                <div key={subscription.id} className="parent-subscription-card">
                  <div className="parent-subscription-header">
                    <div>
                      <h3 className="parent-subscription-name">{subscription.planName}</h3>
                      <p className="parent-subscription-children">
                        {t('payments.forChildren')}: {subscription.children.join(', ')}
                      </p>
                    </div>
                    <span className={`parent-subscription-status ${subscription.status}`}>
                      {subscription.status === 'active' && t('payments.active')}
                      {subscription.status === 'expired' && t('payments.expired')}
                      {subscription.status === 'cancelled' && t('payments.cancelled')}
                    </span>
                  </div>

                  <div className="parent-subscription-details">
                    <div className="parent-subscription-detail">
                      <i className="material-icons">attach_money</i>
                      <div>
                        <p className="parent-detail-label">{t('payments.price')}</p>
                        <p className="parent-detail-value">
                          ${subscription.price} / {subscription.billingCycle === 'monthly' ? t('payments.month') : t('payments.year')}
                        </p>
                      </div>
                    </div>

                    <div className="parent-subscription-detail">
                      <i className="material-icons">calendar_today</i>
                      <div>
                        <p className="parent-detail-label">{t('payments.startDate')}</p>
                        <p className="parent-detail-value">
                          {new Date(subscription.startDate).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    <div className="parent-subscription-detail">
                      <i className="material-icons">event</i>
                      <div>
                        <p className="parent-detail-label">{t('payments.expiryDate')}</p>
                        <p className="parent-detail-value">
                          {new Date(subscription.expiryDate).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    <div className="parent-subscription-detail">
                      <i className="material-icons">autorenew</i>
                      <div>
                        <p className="parent-detail-label">{t('payments.autoRenew')}</p>
                        <p className="parent-detail-value">
                          {subscription.autoRenew ? t('payments.enabled') : t('payments.disabled')}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="parent-subscription-actions">
                    <button className="parent-btn secondary">
                      <i className="material-icons">edit</i>
                      {t('payments.manage')}
                    </button>
                    {subscription.autoRenew && (
                      <button className="parent-btn warning">
                        <i className="material-icons">cancel</i>
                        {t('payments.cancelAutoRenew')}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Payment History Tab */}
      {activeTab === 'history' && (
        <div className="parent-section">
          <div className="parent-section-header">
            <h2 className="parent-section-title">{t('payments.paymentHistory')}</h2>
          </div>

          {paymentHistory.length === 0 ? (
            <div className="parent-empty-state">
              <i className="material-icons">history</i>
              <p>{t('payments.noPayments')}</p>
            </div>
          ) : (
            <div className="parent-payments-table">
              <table>
                <thead>
                  <tr>
                    <th>{t('payments.table.date')}</th>
                    <th>{t('payments.table.description')}</th>
                    <th>{t('payments.table.amount')}</th>
                    <th>{t('payments.table.status')}</th>
                    <th>{t('payments.table.transactionId')}</th>
                    <th>{t('payments.table.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentHistory.map((payment) => (
                    <tr key={payment.id}>
                      <td>{new Date(payment.date).toLocaleDateString()}</td>
                      <td>{payment.description}</td>
                      <td className="parent-amount">${payment.amount}</td>
                      <td>
                        <span className={`parent-payment-status ${payment.status}`}>
                          {payment.status === 'completed' && t('payments.completed')}
                          {payment.status === 'pending' && t('payments.pending')}
                          {payment.status === 'failed' && t('payments.failed')}
                        </span>
                      </td>
                      <td className="parent-transaction-id">{payment.transactionId}</td>
                      <td>
                        <button className="parent-btn-icon" title={t('payments.downloadInvoice')}>
                          <i className="material-icons">download</i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ParentSectionPayments;

